/*
 * 글로벌 소듀 관리자 안에 끼워 넣기 (mj부장님 2026-09-29).
 *
 * 글로벌 소듀(새 관리자)는 샵 화면을 아직 다 만들지 못해서, 한동안 이 몰 관리 화면을
 * iframe 으로 띄워 쓴다. 이때 지킬 것은 셋이다.
 *
 *   1. 로그인: 부모 창이 셀러 토큰을 postMessage 로 넘겨 준다. 다시 로그인하지 않는다.
 *      iframe 안에서는 브라우저가 갱신 쿠키를 막는 경우가 있어, 토큰 갱신도 부모에게 부탁한다.
 *   2. 겉모양: 머리줄, 옆메뉴, 바닥글을 숨긴다. 메뉴는 부모 창이 그린다.
 *   3. 이동 제한: /mall/* 와 /embed/* 밖으로는 못 나간다. 나가려 하면 /embed/mall 로 되돌려 판매자 정보를 연다.
 *
 * 끼워 넣기 모드는 /embed/mall 로 들어올 때 켜지고, 이 창(iframe)의 sessionStorage 에 남는다.
 * 일반 창으로 studio.sodu.pro 를 열면 이 값이 없으므로 아무것도 달라지지 않는다.
 */

const EMBED_KEY = 'sodu-embed';

/** 이 몰 관리 화면을 띄울 수 있는 부모 창 주소. 여기 없는 곳과는 말을 주고받지 않는다 */
const PARENT_ORIGINS: string[] = [
    'https://sodu-global.vercel.app',
    'http://localhost:3010',
    ...String(import.meta.env.VITE_EMBED_PARENT_ORIGINS || '')
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean),
];

/** expiresIn 은 레거시 인증 서버가 주는 꼴 그대로, 만료 시각의 ISO 문자열이다 */
type EmbedToken = { accessToken: string; expiresIn: string; userId: string };

/** 부모 창에서 연 메뉴 이름과 몰 관리 주소 뒷부분. 소도몰 계정은 공구 주소가 다르다 (ManagerLayout 과 같은 규칙) */
const MENU_PATH: Record<string, (isSodomall: boolean) => string> = {
    info: () => '',
    'shipping-policy': () => '/policy/shipping-policy',
    notices: () => '/notices',
    design: () => '/design',
    'tag-manager': () => '/tag-manager',
    products: () => '/products',
    reviews: () => '/reviews',
    'order-groups': () => '/order-groups',
    'shipping-managements': () => '/shipping-managements',
    'issue-orders': () => '/issue-orders',
    'refund-orders': () => '/refund-orders',
    customers: () => '/customers',
    'signup-members': () => '/signup-members',
    inquiries: () => '/inquiries',
    'group-sale-products': (s) => (s ? '/group-sale-products-sodomall' : '/group-sale-products'),
    'group-sales-orders': (s) => (s ? '/group-sales-orders-sodomall-flat' : '/group-sales-orders'),
    sales: (s) => (s ? '/store-statement/sales-list' : '/statement/sales-list'),
};

export function menuPath(mallId: string, menu: string | null, isSodomall: boolean): string {
    const tail = (menu && MENU_PATH[menu]) || MENU_PATH.info;
    return `/mall/${mallId}${tail(isSodomall)}`;
}

function inFrame(): boolean {
    try {
        return window.self !== window.top;
    } catch {
        // 다른 주소의 부모를 읽으려다 막히면 iframe 안이다
        return true;
    }
}

/** 지금 끼워 넣기 모드인가. iframe 안이면서 /embed/mall 로 들어온 적이 있어야 한다 */
export function isEmbedded(): boolean {
    if (!inFrame()) return false;
    try {
        return sessionStorage.getItem(EMBED_KEY) === '1';
    } catch {
        return false;
    }
}

export function markEmbedded(): void {
    if (!inFrame()) return;
    try {
        sessionStorage.setItem(EMBED_KEY, '1');
    } catch {
        // 저장이 막히면 이번 화면만 끼워 넣기로 그린다
    }
}

/**
 * 앱을 처음 그릴 때 쓰는 판정. /embed/mall 로 막 들어온 순간에는 아직 표시가 없어서
 * iframe 안이면서 주소가 /embed/ 로 시작하는 것도 끼워 넣기로 본다.
 */
export function isEmbedAtBoot(): boolean {
    return isEmbedded() || (inFrame() && window.location.pathname.startsWith('/embed/'));
}

/*
 * 화면 테마 (mj부장님 2026-09-29). 글로벌 소듀가 고른 밝게, 어둡게, 시스템을 따른다.
 * 끼워 넣었을 때만 쓰고, 저장 이름도 따로 둔다. studio.sodu.pro 를 그냥 열면 테마는 전과 같다.
 */
export const EMBED_MODE_STORAGE_KEY = 'sodu-embed-mui-mode';
export const EMBED_COLOR_SCHEME_STORAGE_KEY = 'sodu-embed-mui-color-scheme';

export type EmbedTheme = 'light' | 'dark' | 'system';

/** 부모 창이 테마를 알려 줄 때 */
export function onEmbedTheme(apply: (mode: EmbedTheme) => void): () => void {
    function onMessage(ev: MessageEvent) {
        if (!PARENT_ORIGINS.includes(ev.origin)) return;
        if (ev.source !== window.parent) return;
        const d = ev.data;
        if (!d || d.type !== 'sodu-embed:theme') return;
        if (d.mode === 'light' || d.mode === 'dark' || d.mode === 'system') apply(d.mode);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
}

/** 끼워 넣기 모드에서 머물 수 있는 주소인가 */
export function isAllowedEmbedPath(pathname: string): boolean {
    return pathname.startsWith('/mall/') || pathname.startsWith('/embed/');
}

function postToParent(message: Record<string, unknown>): void {
    // 부모 주소를 모르므로 허용 목록마다 보낸다. 주소가 다르면 브라우저가 조용히 버린다
    for (const origin of PARENT_ORIGINS) {
        try {
            window.parent.postMessage(message, origin);
        } catch {
            // 한 곳이 실패해도 나머지는 보낸다
        }
    }
}

let pending: Promise<EmbedToken> | null = null;

/**
 * 부모 창에 셀러 토큰을 달라고 한다. 처음 들어올 때와 토큰이 끝났을 때 부른다.
 * 동시에 여러 번 불러도 요청은 하나만 나간다.
 */
export function requestEmbedToken(timeoutMs = 10000): Promise<EmbedToken> {
    if (pending) return pending;
    pending = new Promise<EmbedToken>((resolve, reject) => {
        const id = Math.random().toString(36).slice(2);
        const timer = window.setTimeout(() => {
            window.removeEventListener('message', onMessage);
            reject(new Error('embed token timeout'));
        }, timeoutMs);

        function onMessage(ev: MessageEvent) {
            if (!PARENT_ORIGINS.includes(ev.origin)) return;
            if (ev.source !== window.parent) return;
            const d = ev.data;
            if (!d || d.type !== 'sodu-embed:token' || d.id !== id) return;
            window.clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            if (!d.accessToken || !d.userId || !d.expiresIn) {
                reject(new Error('embed token empty'));
                return;
            }
            resolve({ accessToken: d.accessToken, expiresIn: String(d.expiresIn || ''), userId: String(d.userId) });
        }

        window.addEventListener('message', onMessage);
        postToParent({ type: 'sodu-embed:need-token', id });
    }).finally(() => {
        pending = null;
    });
    return pending;
}

/** 로그인이 끝나 다시 받아야 할 때 부모 창에 알린다. 부모가 iframe 을 새로 연다 */
export function announceEmbedExpired(): void {
    postToParent({ type: 'sodu-embed:expired' });
}

/**
 * 화면을 불러오는 중인지 부모 창에 알린다. 끼워 넣었을 때는 레거시 로딩 그림을 그리지 않고
 * 글로벌 소듀의 로딩 화면(프로필링크와 같은 것)을 쓴다 (mj부장님 2026-09-29).
 */
export function announceEmbedLoading(on: boolean): void {
    postToParent({ type: 'sodu-embed:loading', on });
}

/** 몰 관리 주소는 아니지만 보여 줄 화면(들어오기 실패 안내)이 떴다고 알린다. 부모 창이 로딩을 걷는다 */
export function announceEmbedShown(): void {
    postToParent({ type: 'sodu-embed:shown' });
}

/** 지금 보고 있는 몰 관리 주소를 부모 창에 알린다. 부모가 왼쪽 메뉴 강조를 맞춘다 */
export function announceEmbedPath(pathname: string): void {
    postToParent({ type: 'sodu-embed:path', path: pathname });
}

/** 부모 창이 왼쪽 메뉴를 눌렀을 때. iframe 을 새로 열지 않고 안에서 옮겨 간다 */
export function onEmbedGo(go: (menu: string) => void): () => void {
    function onMessage(ev: MessageEvent) {
        if (!PARENT_ORIGINS.includes(ev.origin)) return;
        if (ev.source !== window.parent) return;
        const d = ev.data;
        if (!d || d.type !== 'sodu-embed:go' || typeof d.menu !== 'string') return;
        go(d.menu);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
}
